import { FormatMessage, MemberIntroProfile } from "./types";

export const hasMemberIntroContent = (
  intro: MemberIntroProfile | null | undefined
): boolean => {
  if (!intro) {
    return false;
  }
  return Boolean(
    intro.profile_picture ||
      intro.gender ||
      intro.age ||
      intro.ethnicity ||
      intro.sexual_orientation ||
      intro.languages?.length ||
      intro.country ||
      intro.about_me
  );
};

const displayLabel = (value: string | null | undefined): string => {
  const text = (value || "").trim();
  if (!text) {
    return "";
  }
  if (/[A-Z]/.test(text)) {
    return text;
  }
  return text
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, char => char.toUpperCase());
};

export const memberIntroDetailLines = (
  intro: MemberIntroProfile | null | undefined,
  formatMessage: FormatMessage
): string[] => {
  if (!intro) {
    return [];
  }
  const lines: string[] = [];
  const meta = memberIntroMeta(intro, formatMessage);
  if (meta) {
    lines.push(meta);
  }
  const attributes = [
    displayLabel(intro.ethnicity),
    displayLabel(intro.sexual_orientation),
  ]
    .filter(Boolean)
    .join(" • ");
  if (attributes) {
    lines.push(attributes);
  }
  const languages = (intro.languages || [])
    .map(displayLabel)
    .filter(Boolean);
  if (languages.length) {
    lines.push(languages.join(", "));
  }
  return lines;
};

export const memberIntroDisplayName = (
  intro: MemberIntroProfile | null | undefined,
  username?: string | null
): string => {
  const name = intro?.handle?.trim();
  if (!name) {
    return "";
  }
  if (username && name.toLowerCase() === username.toLowerCase()) {
    return "";
  }
  return name;
};

export const memberIntroGenderLabel = (
  gender: string | null | undefined,
  formatMessage: FormatMessage
): string => {
  switch ((gender || "").toLowerCase()) {
    case "male":
    case "m":
    case "man":
      return formatMessage({
        id: "member-intro.gender.male",
        defaultMessage: "Male",
      });
    case "female":
    case "f":
    case "woman":
      return formatMessage({
        id: "member-intro.gender.female",
        defaultMessage: "Female",
      });
    case "trans":
      return formatMessage({
        id: "member-intro.gender.trans",
        defaultMessage: "Trans",
      });
    default:
      return "";
  }
};

export const memberIntroMeta = (
  intro: MemberIntroProfile | null | undefined,
  formatMessage: FormatMessage
): string => {
  if (!intro) {
    return "";
  }
  const parts: string[] = [];
  if (intro.age) {
    parts.push(String(intro.age));
  }
  const gender =
    memberIntroGenderLabel(intro.gender, formatMessage) ||
    displayLabel(intro.gender);
  if (gender) {
    parts.push(gender);
  }
  return parts.join(" • ");
};

const MALE_DEFAULT_IMAGE =
  "https://img.securedataimages.com/images/cams/m_default_640.gif";
const FEMALE_DEFAULT_IMAGE =
  "https://img.securedataimages.com/images/streamray/streams/default_640.gif";

export const memberIntroFallbackPhoto = (
  gender?: string | null
): string => {
  const kind = (gender || "").toLowerCase();
  if (kind === "male" || kind === "m" || kind === "man") {
    return MALE_DEFAULT_IMAGE;
  }
  if (kind === "female" || kind === "f" || kind === "woman") {
    return FEMALE_DEFAULT_IMAGE;
  }
  return "";
};

export const memberGiftLabel = (name: string | null | undefined): string => {
  const label = (name || "")
    .replace(/^virtual_gift_/i, "")
    .replace(/[_-]+/g, " ")
    .trim();
  if (!label) {
    return "";
  }
  return label.replace(/\b\w/g, char => char.toUpperCase());
};
